<template>
  <div>
    <content-loading v-if="loading" />

    <div v-if="!loading && dashboard">
      <!-- All 4 charts in one row -->
      <div class="row dashboard-row">
        <!-- Average Risk Score Gauge -->
        <div class="col-md-3 text-center">
          <div class="card dashboard-card">
            <div class="card-body">
              <h5 class="card-title">{{ $t("risk.dashboard.average-risk") }}</h5>
              <div v-if="dashboard.ratingDefinition" class="chart-container">
                <vue-gauge :refid="'gauge-avg-risk'" :options="gaugeOptions"></vue-gauge>
                <h5 class="mt-2">{{ $t("risk.dashboard.average-score") }}: {{ gaugeOptions.needleValue }}</h5>
              </div>
              <div v-else>
                <h5 class="text-muted mt-4">{{ $t("risk.dashboard.no-data") }}</h5>
              </div>
            </div>
          </div>
        </div>

        <!-- Category-based Pie Chart -->
        <div class="col-md-3 text-center">
          <div class="card dashboard-card">
            <div class="card-body">
              <h5 class="card-title">{{ $t("risk.dashboard.category-based-risk-status") }}</h5>
              <doughnut-chart
                v-if="categoryChartData.datas && categoryChartData.datas.length > 0"
                class="mt-2"
                :height="250"
                :plugins="doughnutPlugins"
                :labels="categoryChartData.labels"
                :datas="categoryChartData.datas"
              />
              <div v-else>
                <h5 class="text-muted mt-4">{{ $t("risk.dashboard.no-data") }}</h5>
              </div>
            </div>
          </div>
        </div>

        <!-- Type-based Pie Chart -->
        <div class="col-md-3 text-center">
          <div class="card dashboard-card">
            <div class="card-body">
              <h5 class="card-title">{{ $t("risk.dashboard.type-based-risk-status") }}</h5>
              <doughnut-chart
                v-if="typeChartData.datas && typeChartData.datas.length > 0"
                class="mt-2"
                :height="250"
                :plugins="doughnutPlugins"
                :labels="typeChartData.labels"
                :datas="typeChartData.datas"
                :backgroundColors="typeColors"
              />
              <div v-else>
                <h5 class="text-muted mt-4">{{ $t("risk.dashboard.no-data") }}</h5>
              </div>
            </div>
          </div>
        </div>

        <!-- Status Summary -->
        <div class="col-md-3 text-center">
          <div class="card dashboard-card">
            <div class="card-body">
              <h5 class="card-title">{{ $t("risk.dashboard.statuses") }}</h5>
              <doughnut-chart
                v-if="statusChartData.datas && statusChartData.datas.length > 0"
                class="mt-2"
                :height="250"
                :plugins="doughnutPlugins"
                :labels="statusChartData.labels"
                :datas="statusChartData.datas"
                :backgroundColors="statusColors"
              />
              <div v-else>
                <h5 class="text-muted mt-4">{{ $t("risk.dashboard.no-data") }}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Risk Score Bar Chart Restored -->
      <div class="row mt-4">
        <div class="col-md-12">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">{{ $t("risk.dashboard.risk-score-chart") }}</h5>
              <div v-if="riskScoreChartData">
                <chartist ratio="ct-chart" :data="riskScoreChartData" :options="barChartOptions" type="Bar"></chartist>
                <div class="legend mt-3">
                  <div class="legend-item" v-for="(item, index) in riskScoreLegend" :key="index">
                    <div class="box" :style="{ backgroundColor: item.color }"></div>
                    <span>{{ item.label }}: {{ item.count }}</span>
                  </div>
                </div>
              </div>
              <div v-else>
                <h5 class="text-muted mt-4">{{ $t("risk.dashboard.no-data") }}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <empty v-if="!loading && !dashboard">
      <h5 class="text-center">{{ $t("risk.dashboard.no-data") }}</h5>
    </empty>
  </div>
</template>

<script>
import DoughnutChart from "@modules/core/organization/components/doughnut";
import ChartDataLabels from "chartjs-plugin-datalabels";
import VueGauge from "vue-gauge";

export default {
  components: { DoughnutChart, VueGauge },
  data() {
    return {
      doughnutPlugins: [ChartDataLabels],
      typeColors: ["#586aff", "#f4a12c", "#50CD89", "#FF7C48", "#2A3042", "#8B5CF6", "#EC4899", "#14B8A6"],
      statusColors: ["#50CD89", "#f46a6a", "#f4a12c", "#586aff", "#2A3042", "#FF7C48", "#8B5CF6", "#EC4899", "#14B8A6", "#6366F1"],
      barChartOptions: {
        height: 300,
        seriesBarDistance: 15,
        axisY: {
          offset: 30,
          onlyInteger: true,
        },
      },
    };
  },
  async mounted() {
    await this.$store.dispatch("organizationRisk/dashboard");
  },
  computed: {
    loading() {
      return this.$store.getters["organizationRisk/isLoading"];
    },
    dashboard() {
      return this.$store.getters["organizationRisk/dashboard"];
    },

    gaugeOptions() {
      if (!this.dashboard || !this.dashboard.ratingDefinition) {
        return { needleValue: 0, arcDelimiters: [33, 66], arcColors: ["#50ce89", "#ffbd89", "#f46a6a"], rangeLabel: ["0", "25"], chartWidth: 220 };
      }
      const needleValue = Math.round(this.dashboard.averageRiskPoint) || 0;
      const rd = this.dashboard.ratingDefinition;
      const firstDelimiter = rd.medium?.min || 33;
      const secondDelimiter = rd.high?.min || 66;

      return {
        needleValue,
        arcDelimiters: [firstDelimiter, secondDelimiter],
        arcColors: ["#50ce89", "#ffbd89", "#f46a6a"],
        rangeLabel: ["0", "25"],
        chartWidth: 300,
      };
    },

    categoryChartData() {
      if (!this.dashboard || !this.dashboard.categoryBasedRiskCount) {
        return { labels: [], datas: [] };
      }
      const labels = Object.keys(this.dashboard.categoryBasedRiskCount);
      const datas = Object.values(this.dashboard.categoryBasedRiskCount);
      return { labels, datas };
    },

    typeChartData() {
      if (!this.dashboard || !this.dashboard.typeBasedRiskCount) {
        return { labels: [], datas: [] };
      }
      const labels = Object.keys(this.dashboard.typeBasedRiskCount);
      const datas = Object.values(this.dashboard.typeBasedRiskCount);
      return { labels, datas };
    },

    riskScoreChartData() {
      if (!this.dashboard || !this.dashboard.riskScores || this.dashboard.riskScores.length === 0) {
        return null;
      }
      const rd = this.dashboard.ratingDefinition;
      const lowMin = rd?.low?.min || 10;
      const mediumMin = rd?.medium?.min || 18;
      const highMin = rd?.high?.min || 27;
      const highestMin = rd?.highest?.min || 52;

      let lowest = 0;
      let low = 0;
      let medium = 0;
      let high = 0;
      let highest = 0;

      this.dashboard.riskScores.forEach((score) => {
        if (score < lowMin) {
          lowest++;
        } else if (score < mediumMin) {
          low++;
        } else if (score < highMin) {
          medium++;
        } else if (score < highestMin) {
          high++;
        } else {
          highest++;
        }
      });

      return {
        labels: [
          this.$t("risk.dashboard.lowest-risk"),
          this.$t("risk.dashboard.low-risk"),
          this.$t("risk.dashboard.medium-risk"),
          this.$t("risk.dashboard.high-risk"),
          this.$t("risk.dashboard.highest-risk"),
        ],
        // Each value in its own series to get its own color category in Chartist
        series: [
          [lowest, null, null, null, null],
          [null, low, null, null, null],
          [null, null, medium, null, null],
          [null, null, null, high, null],
          [null, null, null, null, highest],
        ],
      };
    },

    riskScoreLegend() {
      if (!this.riskScoreChartData) return [];
      return [
        { label: this.$t("risk.dashboard.lowest-risk"), count: this.riskScoreChartData.series[0][0], color: "#14B8A6" },
        { label: this.$t("risk.dashboard.low-risk"), count: this.riskScoreChartData.series[1][1], color: "#50ce89" },
        { label: this.$t("risk.dashboard.medium-risk"), count: this.riskScoreChartData.series[2][2], color: "#ffbd89" },
        { label: this.$t("risk.dashboard.high-risk"), count: this.riskScoreChartData.series[3][3], color: "#f4a12c" },
        { label: this.$t("risk.dashboard.highest-risk"), count: this.riskScoreChartData.series[4][4], color: "#f46a6a" },
      ];
    },

    statusChartData() {
      if (!this.dashboard || !this.dashboard.statusCounts) {
        return { labels: [], datas: [] };
      }
      const statusMap = this.dashboard.statusCounts;
      const labels = Object.keys(statusMap).map((key) => {
        return this.$t(`risk.status.${key.toLowerCase()}`);
      });
      const datas = Object.values(statusMap);
      return { labels, datas };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./decision/organization-risk-decision.scss";

.dashboard-row {
  display: flex;
  flex-wrap: wrap;
}

.dashboard-card {
  height: 380px;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  .card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.chart-container {
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.box {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

/* Chartist Bar Colors */
::v-deep {
  .ct-series-a .ct-bar {
    stroke: #14b8a6 !important;
  }
  .ct-series-b .ct-bar {
    stroke: #50ce89 !important;
  }
  .ct-series-c .ct-bar {
    stroke: #ffbd89 !important;
  }
  .ct-series-d .ct-bar {
    stroke: #f4a12c !important;
  }
  .ct-series-e .ct-bar {
    stroke: #f46a6a !important;
  }
  .ct-bar {
    stroke-width: 40px;
  }
}
</style>
